import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert } from './ui/alert';
import {
  Users,
  Clock,
  CheckCircle,
  MessageCircle,
  MessagesSquare,
  Loader2,
} from 'lucide-react';
import { adminService, AdminStats } from '../services/admin';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactElement;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card className='card-enhanced hover-lift shadow-lg border-0 h-full'>
    <CardContent className='p-4 sm:p-6'>
      <div className='flex items-center justify-between'>
        <div className='flex-1 min-w-0'>
          <p className='text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2 truncate'>
            {title}
          </p>
          <p className='text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 transition-all duration-300'>
            {(value ?? 0).toLocaleString()}
          </p>
          {/* <div className='w-full bg-gray-200 rounded-full h-1 mt-2'>
            <div
              className='bg-gradient-to-r from-primary to-blue-600 h-1 rounded-full transition-all duration-1000'
              style={{ width: '75%' }}
            ></div>
          </div> */}
        </div>
        <div
          className={`p-3 sm:p-4 rounded-xl shadow-lg ${color} flex-shrink-0 ml-4`}
        >
          <div className='w-5 h-5 sm:w-6 sm:h-6'>{icon}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboardOverview: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await adminService.getStats();
        setStats(data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className='space-y-6'>
        <div className='space-y-2'>
          <div className='loading-skeleton h-8 w-64 rounded'></div>
          <div className='loading-skeleton h-4 w-96 rounded'></div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6'>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className='card-enhanced'>
              <CardContent className='p-6'>
                <div className='flex items-center justify-between'>
                  <div className='space-y-3 flex-1'>
                    <div className='loading-skeleton h-4 w-20 rounded'></div>
                    <div className='loading-skeleton h-8 w-16 rounded'></div>
                  </div>
                  <div className='loading-skeleton w-12 h-12 rounded-xl'></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='mb-6'>
        <div className='alert-enhanced alert-error'>
          <div className='flex items-center gap-2'>
            <span className='text-red-500'>⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className='space-y-6 sm:space-y-8'>
      {/* Header with enhanced typography */}
      <div className='space-y-2'>
        <h1 className='heading-1 text-gray-900'>Admin Dashboard</h1>
        <p className='body-large text-gray-600'>
          Overview of system statistics and activity
        </p>
        <div className='flex items-center gap-2 text-sm text-gray-500'>
          <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
          <span>
            Real-time data • Last updated: {new Date().toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Enhanced Stats Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6'>
        <StatCard
          title='Total Users'
          value={stats?.users?.total ?? 0}
          icon={<Users className='w-4 h-4 sm:w-5 sm:h-5 text-white' />}
          color='bg-blue-500'
        />

        <StatCard
          title='Synced Users'
          value={stats?.users?.syncedUsers ?? 0}
          icon={<CheckCircle className='w-4 h-4 sm:w-5 sm:h-5 text-white' />}
          color='bg-green-500'
        />

        {/* <StatCard
          title='Local Users'
          value={Math.max(
            0,
            (stats?.users?.total ?? 0) - (stats?.users?.syncedUsers ?? 0)
          )}
          icon={<Clock className='w-4 h-4 sm:w-5 sm:h-5 text-white' />}
          color='bg-orange-500'
        /> */}

        <StatCard
          title='Chat Sessions'
          value={stats?.chat?.totalSessions ?? 0}
          icon={<MessageCircle className='w-4 h-4 sm:w-5 sm:h-5 text-white' />}
          color='bg-purple-500'
        />
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6'>
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Users className='w-5 h-5' />
              User Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>Total Users</span>
                <span className='font-semibold'>
                  {stats?.users?.total ?? 0}
                </span>
              </div>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>Synced Users</span>
                <span className='font-semibold'>
                  {stats?.users?.syncedUsers ?? 0}
                </span>
              </div>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>Sync Rate</span>
                <span className='font-semibold'>
                  {(stats?.users?.total ?? 0) > 0
                    ? Math.round(
                        ((stats?.users?.syncedUsers ?? 0) /
                          (stats?.users?.total ?? 1)) *
                          100
                      )
                    : 0}
                  %
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <MessagesSquare className='w-5 h-5' />
              Chat Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>Total Sessions</span>
                <span className='font-semibold'>
                  {stats?.chat?.totalSessions ?? 0}
                </span>
              </div>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>Total Messages</span>
                <span className='font-semibold'>
                  {stats?.chat?.totalMessages ?? 0}
                </span>
              </div>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>
                  Avg Messages/Session
                </span>
                <span className='font-semibold'>
                  {(stats?.chat?.totalSessions ?? 0) > 0
                    ? Math.round(
                        (stats?.chat?.totalMessages ?? 0) /
                          (stats?.chat?.totalSessions ?? 1)
                      )
                    : 0}
                </span>
              </div>
              <div className='flex justify-between items-center'>
                <span className='text-sm text-gray-600'>Active Sessions</span>
                <span className='font-semibold'>
                  {stats?.chat?.activeSessions ?? 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboardOverview;
